import * as React from 'react'
import { AaaCommentType } from 'types/aaa'
import { SoftService, Tow, TowExchange } from 'types/callstore'
import { addRideAlong, locationToAAALocation } from 'utils/aaa'
import { StandardServiceCall } from 'types/aaa'
import { useServiceProxy } from 'hooks/kong'
import { useDispatch, useSelector } from 'react-redux'
import { selectAllEvents, selectRecentDispatches } from 'redux/appStore'
import { isEmpty } from 'lodash-es'
import { IncomingStandardServiceCall } from 'types/api'
import { deepMapSanitize } from 'utils/deepMap'
import { AxiosResponse } from 'axios'
import { addRecentAAADispatch } from 'redux/recentDispatch/recentDispatchSlice'

export function useAAADispatchQuery(callKey?: string | null) {
  const dispatch = useDispatch()
  const dispatches = useSelector(selectRecentDispatches)
  const addRecentDispatch = (newDispatch: StandardServiceCall) => dispatch(addRecentAAADispatch(newDispatch))
  const serviceProxy = useServiceProxy()
  const allEvents = useSelector(selectAllEvents)
  const [error, setError] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState<boolean>(false)
  const [data, setData] = React.useState<StandardServiceCall | null>(null)

  const shouldFireQuery = React.useRef<boolean>(false)

  const query = React.useCallback(
    async (shouldFireQuery: boolean) => {
      if (shouldFireQuery) {
        try {
          setLoading(true)
          setError(null)
          const url = `/serviceproxy/aaa/dispatch/${callKey}`
          const inc = await serviceProxy<IncomingStandardServiceCall>('get', url)
          const response = { ...inc, data: deepMapSanitize(inc.data) } as AxiosResponse<StandardServiceCall>

          if (!response) {
            setLoading(false)
            throw new Error('No Response found')
          }

          if (!response.status || response.status !== 200) {
            setLoading(false)
            throw new Error(`Failed call ${response.status}`)
          }

          if (response?.data) {
            setData(response.data)
            setLoading(false)
          }
        } catch (e) {
          console.error(e)
          setLoading(false)
          setError('Unable to lookup AAA dispatch')
        }
      }
    },
    [callKey, serviceProxy],
  )

  React.useEffect(() => {
    let recentDispatch = dispatches.AAADispatches.find((disp) => disp.callKey === callKey)

    if (recentDispatch !== undefined) {
      setData(recentDispatch)
      setLoading(false)
      shouldFireQuery.current = false
    } else {
      setData(null)
      shouldFireQuery.current = true
    }

    if (data && dispatches.AAADispatches.find((disp) => disp.callKey === callKey) === undefined) {
      addRecentDispatch(data)
    }
  }, [shouldFireQuery, callKey, data, dispatches])

  React.useEffect(() => {
    async function fetchAAAData() {
      const reduxCallKeyEvent = Object.entries(allEvents).filter(([key, value]) => {
        const event = value?.event
        if (event && event.attributes && event.attributes.dispatch) {
          const dispatch = event.attributes.dispatch[0] as StandardServiceCall
          if (dispatch.callKey === callKey) return { ...dispatch }
        }
        return null
      })[0]

      if (error || loading) return

      if (!isEmpty(reduxCallKeyEvent)) {
        const reduxEvent = reduxCallKeyEvent[1]
        reduxEvent && setData(reduxEvent?.event?.attributes.dispatch[0])
        shouldFireQuery.current = false
        return
      }

      if (callKey && data === null) {
        await query(shouldFireQuery.current)
        shouldFireQuery.current = false
      }
    }

    shouldFireQuery && fetchAAAData()
  }, [callKey, shouldFireQuery.current, data, query, error, loading, allEvents])

  return { data, loading, error, refetch: query }
}

function useAAADispatch(callKey?: string | null) {
  const { data: standardServiceCall, loading, refetch, error } = useAAADispatchQuery(callKey)
  const serviceProxy = useServiceProxy()

  const handleUpdate = async (values: SoftService | Tow | TowExchange) => {
    if (!standardServiceCall || !callKey) throw new Error()
    const { serviceLocations } = values
    let service = {
      ...standardServiceCall,
      service: {
        ...standardServiceCall.service,
        serviceLocations: Object.values(serviceLocations).map((loc) => locationToAAALocation(loc)),
      },
      ...(values.notes && {
        comments: [
          {
            commentType: AaaCommentType.NORMAL,
            text: values.notes,
          },
        ],
      }),
    }

    // @ts-ignore
    service = addRideAlong(service, values)

    try {
      return await serviceProxy<StandardServiceCall>('post', `/serviceproxy/aaa/dispatch/${callKey}`, {}, { callKey, ...service })
    } catch (e) {
      throw new Error('Dispatch failed to update')
    }
  }

  return {
    dispatch: standardServiceCall,
    handleUpdate,
    loading,
    refetch,
    error,
  }
}

export default useAAADispatch
